import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail, formatDate, istMonth } from "@/lib/api";
import { CalendarDays, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
import MonthSelect from "@/components/MonthSelect";

const WEEKDAYS = [
  { d: 0, l: "Mon" }, { d: 1, l: "Tue" }, { d: 2, l: "Wed" },
  { d: 3, l: "Thu" }, { d: 4, l: "Fri" }, { d: 5, l: "Sat" }, { d: 6, l: "Sun" },
];

export default function OfficeClosureTab() {
  const [holidayMonth, setHolidayMonth] = useState(istMonth());
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState({ date: "", name: "" });
  const [offWeekdays, setOffWeekdays] = useState([6]);
  const [savingOff, setSavingOff] = useState(false);
  const [overrides, setOverrides] = useState([]);
  const [overrideForm, setOverrideForm] = useState({ date: "", name: "" });
  const [confirmAction, setConfirmAction] = useState(null);

  const loadHolidays = (m = holidayMonth) => api.get(`/admin/holidays?month=${m}`).then(r => setHolidays(r.data || []));
  const loadWorkingDays = () => api.get("/admin/working-days").then(r => setOffWeekdays(r.data.off_weekdays || []));
  const loadOverrides = (m = holidayMonth) => api.get(`/admin/workday-overrides?month=${m}`).then(r => setOverrides(r.data || []));

  useEffect(() => {
    loadWorkingDays();
    loadHolidays();
    loadOverrides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadHolidays(holidayMonth);
    loadOverrides(holidayMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holidayMonth]);

  const toggleOffWeekday = (day) => {
    setOffWeekdays(offWeekdays.includes(day)
      ? offWeekdays.filter(d => d !== day)
      : [...offWeekdays, day]);
  };

  const saveOffWeekdays = async () => {
    setSavingOff(true);
    try {
      await api.put("/admin/working-days", { off_weekdays: offWeekdays });
      toast.success("Office-closure days saved");
      loadWorkingDays();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
    finally { setSavingOff(false); }
  };

  const addHoliday = async (e) => {
    e.preventDefault();
    if (!newHoliday.date) { toast.error("Pick a date"); return; }
    try {
      await api.post("/admin/holidays", newHoliday);
      toast.success("Holiday added");
      setNewHoliday({ date: "", name: "" });
      loadHolidays();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
  };

  const removeHoliday = async (h) => {
    try {
      await api.delete(`/admin/holidays/${h.id}`);
      loadHolidays();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
  };

  const addWorkingOverride = async (e) => {
    e.preventDefault();
    if (!overrideForm.date) { toast.error("Pick a date"); return; }
    try {
      await api.put("/admin/workday-overrides", {
        date: overrideForm.date,
        is_working: true,
        name: overrideForm.name || "Working day override",
      });
      toast.success("Working day override saved");
      setOverrideForm({ date: "", name: "" });
      loadOverrides();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
  };

  const removeOverride = async (o) => {
    try {
      await api.delete(`/admin/workday-overrides/${o.id}`);
      loadOverrides();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
  };

  const runConfirmAction = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (action?.type === "holiday") await removeHoliday(action.item);
    if (action?.type === "override") await removeOverride(action.item);
  };

  return (
    <div className="space-y-6" data-testid="admin-office-closure">
      <div>
        <div className="text-overline">Calendar</div>
        <h2 className="text-2xl font-light text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Office closure</h2>
      </div>

      <div className="card-warm p-6 space-y-4" data-testid="office-closure-weekly">
        <div>
          <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Weekly office closure</h3>
          <p className="text-sm text-[#5C635E]">Tick the weekdays your office stays closed. Selected days are treated as paid holidays unless a specific date is marked as working below.</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="off-weekdays-picker">
          {WEEKDAYS.map(({ d, l }) => {
            const on = offWeekdays.includes(d);
            return (
              <button key={d} type="button" data-testid={`off-weekday-${d}`}
                onClick={() => toggleOffWeekday(d)}
                className={`px-4 py-2 rounded-full text-sm border transition ${
                  on
                    ? "bg-[#4A5D4E] text-white border-[#4A5D4E]"
                    : "bg-[#FDFBF7] text-[#2D312E] border-[#EAE6DF] hover:border-[#4A5D4E]"
                }`}>
                {l}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="save-off-weekdays" onClick={saveOffWeekdays} disabled={savingOff}
            className="bg-[#4A5D4E] hover:bg-[#3B4A3E] disabled:opacity-60 text-white rounded-xl px-6 py-2.5 text-sm">
            {savingOff ? "Saving..." : "Save"}
          </button>
          <span className="text-xs text-[#8A938D]">{offWeekdays.length} day{offWeekdays.length === 1 ? "" : "s"} off per week</span>
        </div>
      </div>

      <div className="card-warm p-6 space-y-4" data-testid="office-closure-overrides">
        <div>
          <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Working day overrides</h3>
          <p className="text-sm text-[#5C635E]">Use this when a normally closed weekday should be treated as a working day, for example a specific Monday.</p>
        </div>
        <form onSubmit={addWorkingOverride} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="new-working-override-form">
          <div>
            <label className="text-xs text-[#5C635E] mb-1 block">Date*</label>
            <input data-testid="working-override-date" type="date" value={overrideForm.date}
              onChange={(e) => setOverrideForm({ ...overrideForm, date: e.target.value })}
              className="w-full bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-[#5C635E] mb-1 block">Note</label>
            <input data-testid="working-override-name" value={overrideForm.name}
              onChange={(e) => setOverrideForm({ ...overrideForm, name: e.target.value })}
              placeholder="e.g. Special working Monday"
              className="w-full bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm" />
          </div>
          <button data-testid="add-working-override" type="submit"
            className="bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white rounded-lg px-4 py-2 text-sm flex items-center justify-center gap-2">
            <CalendarDays className="h-4 w-4" /> Mark working
          </button>
        </form>
        <SimpleList items={overrides} empty="No working overrides for this month." remove={(item) => setConfirmAction({ type: "override", item })} kind="working" />
      </div>

      <div className="card-warm p-6 space-y-4" data-testid="office-closure-holidays">
        <div>
          <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Bank holidays</h3>
          <p className="text-sm text-[#5C635E]">Configure paid holidays. Days marked here do not count as absent and are excluded from daily incentive computation.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-[#5C635E]">Month</label>
          <MonthSelect testId="holiday-month" value={holidayMonth} onChange={setHolidayMonth} />
          <span className="text-xs text-[#8A938D] ml-auto">{holidays.length} holiday{holidays.length === 1 ? "" : "s"} this month</span>
        </div>
        <form onSubmit={addHoliday} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="new-holiday-form">
          <div>
            <label className="text-xs text-[#5C635E] mb-1 block">Date*</label>
            <input data-testid="new-holiday-date" type="date" value={newHoliday.date}
              onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
              className="w-full bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-[#5C635E] mb-1 block">Name</label>
            <input data-testid="new-holiday-name" value={newHoliday.name}
              onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
              placeholder="e.g. Diwali"
              className="w-full bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm" />
          </div>
          <button data-testid="add-holiday" type="submit"
            className="bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white rounded-lg px-4 py-2 text-sm">Add holiday</button>
        </form>
        <SimpleList items={holidays} empty="No holidays for this month." remove={(item) => setConfirmAction({ type: "holiday", item })} kind="holiday" />
      </div>
      <ConfirmDialog
        open={!!confirmAction}
        title={confirmAction?.type === "holiday" ? "Remove holiday?" : "Remove working override?"}
        message={confirmAction?.type === "holiday" ? `Remove holiday ${confirmAction?.item?.date}?` : `Remove working override ${confirmAction?.item?.date}?`}
        confirmLabel="Remove"
        onConfirm={runConfirmAction}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}

function SimpleList({ items, empty, remove, kind }) {
  return (
    <div className="space-y-1" data-testid={`${kind}-list`}>
      {items.length === 0 ? (
        <div className="text-sm text-[#8A938D]">{empty}</div>
      ) : items.map((item) => (
        <div key={item.id} data-testid={`${kind}-${item.id}`} className="flex items-center justify-between border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm">
          <div>
            <span className="text-[#2D312E] font-medium">{formatDate(item.date)}</span>
            {item.name && <span className="text-[#5C635E] ml-2">- {item.name}</span>}
          </div>
          <button type="button" data-testid={`remove-${kind}-${item.id}`} onClick={() => remove(item)}
            className="text-[#C05746] text-xs hover:underline inline-flex items-center gap-1">
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </button>
        </div>
      ))}
    </div>
  );
}
